"use client";

import FinanceCard from "@/components/portfolio/FinanceCard";
import PercentageIndicator from "@/components/portfolio/PercentageIndicator";
import PossibleRent from "@/components/portfolio/PossibleRent";
import { TaxOverview } from "@/components/portfolio/transactions/TaxOverview";
import { RoundButtonWithIcon } from "@/components/tools/RoundButtonWithIcon";
import { CardTitle } from "@/components/ui/Card";
import { ChartContainer, ChartTooltip } from "@/components/ui/Chart";
import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import type { Transaction } from "@/types";
import { expandRecurringTransactions } from "@/utils/MyEstateHelper";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/solid";
import * as React from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import { PortfolioEstateAnalyticsPanel } from "@/components/portfolio/portfolio-page/PortfolioEstateAnalyticsPanel";
import { StatusPill } from "@/components/portfolio/StatusPill";
import { Panel, PanelGrid } from "@/components/ui/Panel";

const SectionBadge = ({ children }: { children: React.ReactNode }) => (
  <span className="app-section-eyebrow">{children}</span>
);

function verificationPeriodForDate(date: string) {
  const transactionDate = new Date(date);
  return `${String(transactionDate.getMonth() + 1).padStart(2, "0")} ${transactionDate.getFullYear()}`;
}

function isVerifiedForAnalytics(transaction: Transaction) {
  if (transaction.isPrediction) return true;
  if (transaction.transactionType !== "INCOME") return true;
  return (
    transaction.verification?.listOfVerifiedTransactions?.includes(
      verificationPeriodForDate(transaction.dateFrom),
    ) ?? false
  );
}

const ChartLineMyEstate = () => {
  const { language } = useLanguage();
  const copy = {
    cs: {
      prediction: "Predikce",
      forecastToYearEnd: "Výhled do konce roku",
      forecastDescription:
        "Predikce je počítána do konce roku za předpokladu, že současný stav všech transakcí zůstane nezměněn. Doporučujeme však alespoň jednou ročně upravit výši nájmu podle ukazatele průměrné tržní ceny.",
      actual: "Skutečnost",
      actualOverview: "Přehled evidovaných transakcí",
      actualDescription:
        "Přehled vychází pouze ze skutečně evidovaných příjmů a výdajů. Budoucí opakované transakce nejsou do výsledku zahrnuty.",
      yearlyOverview: "Roční přehled",
      predictionStatus: "Predikce do konce roku",
      closedOverview: "Uzavřený přehled",
      operatingResult: "Výsledek hospodaření",
      yearOverYearChange: "Za 12 měsíců",
      financeHintPrediction: "Souhrn počítá i očekávaný vývoj do konce roku.",
      financeHintClosed: "Souhrn příjmů, výdajů a zisku pro vybraný rok.",
      balanceDevelopment: "Vývoj zůstatku",
      chartHintCurrent: "Graf ukazuje dosavadní průběh transakcí.",
      chartHintTransactions:
        "Najetím na bod uvidíte potvrzené transakce za daný měsíc a průběžný součet.",
      includingForecast: "Včetně predikce",
      fromRealTransactions: "Z reálných transakcí",
      noTransactions: "V tomto měsíci neevidujeme žádné transakce.",
      monthTotal: "Součet měsíce",
      fromYearStart: "Od začátku roku",
      currency: "Kč",
      locale: "cs-CZ",
    },
    en: {
      prediction: "Forecast",
      forecastToYearEnd: "Outlook to year end",
      forecastDescription:
        "The forecast is calculated to the end of the year assuming the current state of all transactions remains unchanged. We still recommend adjusting rent at least once a year according to the average market price indicator.",
      actual: "Actuals",
      actualOverview: "Recorded transactions overview",
      actualDescription:
        "This overview includes only recorded income and expenses. Future recurring transactions are not included in the result.",
      yearlyOverview: "Yearly overview",
      predictionStatus: "Forecast to year end",
      closedOverview: "Closed overview",
      operatingResult: "Operating result",
      yearOverYearChange: "Over 12 months",
      financeHintPrediction:
        "The summary also includes expected development to the end of the year.",
      financeHintClosed:
        "Summary of income, expenses, and profit for the selected year.",
      balanceDevelopment: "Balance development",
      chartHintCurrent: "The chart shows the verified transactions.",
      chartHintTransactions:
        "Hover a point to see verified transactions for that month and the running total.",
      includingForecast: "Including forecast",
      fromRealTransactions: "From actual transactions",
      noTransactions: "No transactions are recorded for this month.",
      monthTotal: "Month total",
      fromYearStart: "Since the start of the year",
      currency: "CZK",
      locale: "en-US",
    },
  }[language];
  const {
    selectedYear,
    setSelectedYear,
    minYear,
    maxYear,
    chartConfig,
    transactions,
    isPredictionView,
    currentYear,
    currentMonth,
    selectedEstate,
  } = useEstateContext();

  const monthLabels = React.useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) =>
        new Date(2000, index, 1).toLocaleString(copy.locale, {
          month: "short",
        }),
      ),
    [copy.locale],
  );

  const monthFullLabels = React.useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) =>
        new Date(2000, index, 1).toLocaleString(copy.locale, {
          month: "long",
        }),
      ),
    [copy.locale],
  );

  const analyticalTransactions = React.useMemo(
    () => transactions.filter(isVerifiedForAnalytics),
    [transactions],
  );

  const analyticalTotals = React.useMemo(() => {
    let income = 0;
    let expense = 0;

    analyticalTransactions
      .filter((t) => t.showInDashboard)
      .forEach((t) => {
        if (t.transactionType === "INCOME") income += t.amount;
        else expense += t.amount;
      });

    return { income, expense, profit: income - expense };
  }, [analyticalTransactions]);

  const analyticalTotalsUpToMonth = React.useMemo(() => {
    if (selectedYear !== currentYear) return analyticalTotals;

    let income = 0;
    let expense = 0;

    analyticalTransactions
      .filter((t) => t.showInDashboard)
      .forEach((t) => {
        const transactionMonth = new Date(t.dateFrom).getMonth() + 1;
        if (transactionMonth <= currentMonth) {
          if (t.transactionType === "INCOME") income += t.amount;
          else expense += t.amount;
        }
      });

    return { income, expense, profit: income - expense };
  }, [
    analyticalTotals,
    analyticalTransactions,
    currentMonth,
    currentYear,
    selectedYear,
  ]);

  const analyticalLastYearProfit = React.useMemo(() => {
    if (!selectedEstate) return 0;

    return expandRecurringTransactions(
      selectedEstate.transactions,
      selectedYear - 1,
      new Date(),
    )
      .filter(isVerifiedForAnalytics)
      .filter((t) => t.showInDashboard)
      .reduce(
        (total, t) =>
          total + (t.transactionType === "INCOME" ? t.amount : -t.amount),
        0,
      );
  }, [selectedEstate, selectedYear]);

  const chartData = React.useMemo(() => {
    const filtered = analyticalTransactions
      .filter((t: any) => new Date(t.dateFrom).getFullYear() === selectedYear)
      .sort(
        (a: any, b: any) =>
          new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime(),
      );
    const monthTransactionsMap: any[][] = Array.from({ length: 12 }, () => []);

    filtered.forEach((transaction: any) => {
      const transactionDate = new Date(transaction.dateFrom);
      monthTransactionsMap[transactionDate.getMonth()].push(transaction);
    });

    let runningTotal = 0;

    return monthTransactionsMap.map((monthTransactions, monthIndex) => {
      const monthTotal = monthTransactions.reduce(
        (sum: number, t: any) =>
          sum + (t.transactionType === "INCOME" ? t.amount : -t.amount),
        0,
      );
      runningTotal += monthTotal;

      return {
        month: monthLabels[monthIndex],
        monthFull: monthFullLabels[monthIndex],
        monthIndex,
        monthPosition: monthIndex,
        balance: runningTotal,
        change: monthTotal,
        monthTransactions,
        monthTotal,
        yearTotal: runningTotal,
      };
    });
  }, [analyticalTransactions, monthFullLabels, monthLabels, selectedYear]);

  const incomeFinal = isPredictionView
    ? analyticalTotals.income
    : analyticalTotalsUpToMonth.income;
  const expenseFinal = isPredictionView
    ? analyticalTotals.expense
    : analyticalTotalsUpToMonth.expense;
  const profitFinal = isPredictionView
    ? analyticalTotals.profit
    : analyticalTotalsUpToMonth.profit;
  const yearStatusLabel =
    isPredictionView && selectedYear === currentYear
      ? copy.predictionStatus
      : copy.closedOverview;
  const financeHint =
    isPredictionView && selectedYear === currentYear
      ? copy.financeHintPrediction
      : copy.financeHintClosed;
  const chartHint =
    selectedYear === currentYear && !isPredictionView
      ? copy.chartHintCurrent
      : copy.chartHintTransactions;
  const showForecast = isPredictionView && selectedYear === maxYear;
  const overviewCopy = showForecast
    ? {
        badge: copy.prediction,
        title: copy.forecastToYearEnd,
        description: copy.forecastDescription,
      }
    : {
        badge: copy.actual,
        title: copy.actualOverview,
        description: copy.actualDescription,
      };
  const displayChartData = React.useMemo(
    () =>
      chartData.map((d) => {
        if (selectedYear === currentYear && !isPredictionView) {
          return d.monthIndex + 1 <= currentMonth ? d : { ...d, balance: null };
        }

        return d;
      }),
    [chartData, currentMonth, currentYear, isPredictionView, selectedYear],
  );
  const chartLineColor = "rgb(var(--watchdog-chart-main))";

  return (
    <Panel as="section" variant="section" className="w-full">
      <PortfolioEstateAnalyticsPanel />
      <div className="space-y-3 p-4 sm:p-5">
        <PanelGrid className="grid-cols-6 md:grid-cols-12">
          <Panel className="col-span-3 md:col-span-6">
            <PossibleRent />
          </Panel>
          <Panel className="col-span-3 md:col-span-6">
            <Panel variant="compact" className="h-full text-foreground">
              <div className="flex h-full flex-col justify-center gap-2">
                <SectionBadge>{overviewCopy.badge}</SectionBadge>
                <p className="app-card-title">{overviewCopy.title}</p>
                <p className="app-card-description">
                  {overviewCopy.description}
                </p>
              </div>
            </Panel>
          </Panel>
          <Panel className="col-span-6 row-span-2 md:col-span-4">
            <TaxOverview />
          </Panel>
          <Panel className="col-span-3 row-span-2 flex flex-col md:col-span-4">
            {/* Top: badge + pill */}
            <div className="portfolio-card__header-cols">
              <SectionBadge>{copy.yearlyOverview}</SectionBadge>
              <StatusPill>{yearStatusLabel}</StatusPill>
            </div>

            {/* Middle: centered content */}
            <div className="flex-1 flex flex-col items-center justify-center gap-20 text-center">
              <CardTitle className="app-panel-title">
                {copy.operatingResult}
              </CardTitle>

              <div className="flex items-center gap-3 mx-auto">
                <RoundButtonWithIcon
                  icon={ArrowLeftIcon}
                  onClick={() => {
                    if (selectedYear !== minYear) {
                      setSelectedYear((prev) => prev - 1);
                    }
                  }}
                  disabled={selectedYear === minYear}
                />
                <span className="text-3xl font-semibold text-softiq-primary dark:text-softiq-secondary">
                  {selectedYear}
                </span>
                <RoundButtonWithIcon
                  icon={ArrowRightIcon}
                  onClick={() => {
                    if (selectedYear !== maxYear) {
                      setSelectedYear((prev) => prev + 1);
                    }
                  }}
                  disabled={selectedYear === maxYear}
                />
              </div>

              {selectedYear !== minYear && (
                <PercentageIndicator
                  valueNow={profitFinal}
                  valueBefore={analyticalLastYearProfit}
                  text={copy.yearOverYearChange}
                />
              )}
            </div>
          </Panel>{" "}
          <Panel className="col-span-3 row-span-2 md:col-span-4">
            <div className="space-y-1">
              <SectionBadge>Cash flow</SectionBadge>
              <p className="app-card-description">{financeHint}</p>
            </div>
            <FinanceCard
              type={"INCOME"}
              value={incomeFinal}
              total={incomeFinal}
            />
            <FinanceCard
              type={"EXPENSE"}
              value={expenseFinal}
              total={profitFinal}
            />
            <FinanceCard
              type={"PROFIT"}
              value={profitFinal}
              total={incomeFinal}
            />
          </Panel>
        </PanelGrid>
        <Panel>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <SectionBadge>{copy.balanceDevelopment}</SectionBadge>
              <p className="app-card-description">{chartHint}</p>
            </div>
            <StatusPill>
              {isPredictionView
                ? copy.includingForecast
                : copy.fromRealTransactions}
            </StatusPill>
          </div>

          <ChartContainer
            config={chartConfig}
            className="aspect-auto h-[300px] w-full"
          >
            <ComposedChart
              data={displayChartData}
              margin={{ left: 12, right: 12 }}
            >
              <defs>
                <linearGradient
                  id="portfolio-balance-chart-fill"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor={chartLineColor}
                    stopOpacity={0.32}
                  />
                  <stop
                    offset="100%"
                    stopColor={chartLineColor}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                vertical={false}
                stroke="currentColor"
                strokeOpacity={0.1}
                strokeDasharray="4 6"
              />
              <XAxis
                dataKey="monthPosition"
                type="number"
                domain={[0, 12]}
                ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]}
                interval={0}
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => monthFullLabels[value] ?? ""}
              />

              <YAxis
                width={80}
                tickLine={false}
                axisLine={false}
                tickMargin={12}
                tick={{
                  fill: "rgb(var(--softiq-primary) / 0.55)",
                  fontSize: 12,
                  fontWeight: 500,
                }}
                tickFormatter={(value) =>
                  `${value.toLocaleString(copy.locale)} ${copy.currency}`
                }
                tickCount={12}
                allowDecimals={false}
                domain={["dataMin - 2000", "dataMax + 2000"]}
              />

              <ChartTooltip
                content={({ payload }) => {
                  if (!payload || !payload.length) return null;

                  const point = payload[0].payload;

                  return (
                    <div className="min-w-[220px] rounded-lg border border-softiq-primary/10 bg-white p-3 text-sm text-foreground shadow-lg dark:border-white/10 dark:bg-softiq-dark-elevated dark:shadow-black/30">
                      <p className="card-window__inner-eyebrow">
                        {point.monthFull}
                      </p>

                      <div className="app-card-description">
                        {point.monthTransactions.length > 0 ? (
                          point.monthTransactions.map((t: any) => {
                            const value =
                              t.transactionType === "INCOME"
                                ? t.amount
                                : -t.amount;

                            return (
                              <div
                                key={t.id}
                                className="flex justify-between gap-4"
                              >
                                <span
                                  className={`${!t.showInDashboard ? "text-softiq-secondary-shade-700 dark:text-softiq-secondary-shade-300" : "text-foreground"}`}
                                >
                                  {t.name}
                                </span>
                                <span
                                  className={`${t.showInDashboard ? "font-extrabold" : "font-normal"} ${
                                    value >= 0
                                      ? "text-green-600 dark:text-emerald-400"
                                      : "text-red-600 dark:text-red-400"
                                  }`}
                                >
                                  {value >= 0 ? "+" : ""}
                                  {value.toLocaleString(copy.locale)}{" "}
                                  {copy.currency}
                                </span>
                              </div>
                            );
                          })
                        ) : (
                          <p className="app-card-description">
                            {copy.noTransactions}
                          </p>
                        )}
                      </div>

                      <hr className="my-2 border-softiq-primary/10 dark:border-white/10" />

                      <div className="app-card-description flex justify-between">
                        <span>{copy.monthTotal}</span>
                        <span>
                          {point.monthTotal.toLocaleString(copy.locale)}{" "}
                          {copy.currency}
                        </span>
                      </div>

                      <div className="app-card-description mt-1 flex justify-between">
                        <span>{copy.fromYearStart}</span>
                        <span>
                          {point.yearTotal.toLocaleString(copy.locale)}{" "}
                          {copy.currency}
                        </span>
                      </div>
                    </div>
                  );
                }}
              />

              <Area
                dataKey="balance"
                type="natural"
                stroke="none"
                fill="url(#portfolio-balance-chart-fill)"
                fillOpacity={1}
                isAnimationActive={false}
              />

              <Line
                dataKey="balance"
                type="natural"
                stroke={chartLineColor}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={false}
                activeDot={{
                  r: 5,
                  fill: "hsl(var(--background))",
                  stroke: chartLineColor,
                  strokeWidth: 2,
                }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ChartContainer>
        </Panel>
      </div>
    </Panel>
  );
};

export default ChartLineMyEstate;
